<script setup>
import { computed } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Avatar from 'next/avatar/Avatar.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import BaseTable from 'dashboard/components-next/table/BaseTable.vue';
import BaseTableRow from 'dashboard/components-next/table/BaseTableRow.vue';
import BaseTableCell from 'dashboard/components-next/table/BaseTableCell.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  agentList: {
    type: Array,
    default: () => [],
  },
  selectedAgents: {
    type: Array,
    default: () => [],
  },
  updateSelectedAgents: {
    type: Function,
    default: () => {},
  },
  isWorking: {
    type: Boolean,
    default: false,
  },
  submitButtonText: {
    type: String,
    default: '',
  },
  searchQuery: {
    type: String,
    default: '',
  },
  showSelectedCount: {
    type: Boolean,
    default: true,
  },
  noDataMessage: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();

const selectedAgentCount = computed(() => props.selectedAgents.length);

const normalizedSearchQuery = computed(() =>
  props.searchQuery.trim().toLocaleLowerCase()
);

const visibleAgents = computed(() => {
  if (!normalizedSearchQuery.value) return props.agentList;

  return props.agentList.filter(agent => {
    const name = String(agent.name || '').toLocaleLowerCase();
    const email = String(agent.email || '').toLocaleLowerCase();
    return (
      name.includes(normalizedSearchQuery.value) ||
      email.includes(normalizedSearchQuery.value)
    );
  });
});

const visibleAgentIds = computed(() =>
  visibleAgents.value.map(agent => agent.id)
);

const allAgentsSelected = computed(
  () =>
    visibleAgentIds.value.length > 0 &&
    visibleAgentIds.value.every(agentId =>
      props.selectedAgents.includes(agentId)
    )
);

const someAgentsSelected = computed(
  () =>
    visibleAgentIds.value.some(agentId =>
      props.selectedAgents.includes(agentId)
    ) && !allAgentsSelected.value
);

const disableSubmitButton = computed(() => selectedAgentCount.value === 0);

const isAgentSelected = agentId => {
  return props.selectedAgents.includes(agentId);
};

const handleSelectAgent = agentId => {
  const shouldRemove = isAgentSelected(agentId);

  let result = [];
  if (shouldRemove) {
    result = props.selectedAgents.filter(item => item !== agentId);
  } else {
    result = [...props.selectedAgents, agentId];
  }

  props.updateSelectedAgents(result);
};

const toggleSelectAll = () => {
  if (allAgentsSelected.value) {
    props.updateSelectedAgents(
      props.selectedAgents.filter(
        agentId => !visibleAgentIds.value.includes(agentId)
      )
    );
  } else {
    props.updateSelectedAgents([
      ...new Set([...props.selectedAgents, ...visibleAgentIds.value]),
    ]);
  }
};

const headers = computed(() => [
  '',
  t('TEAMS_SETTINGS.AGENTS.AGENT'),
  t('TEAMS_SETTINGS.AGENTS.EMAIL'),
]);
</script>

<template>
  <BaseTable
    :headers="headers"
    :items="visibleAgents"
    :no-data-message="noDataMessage"
  >
    <template #header-0>
      <div class="flex items-center">
        <Checkbox
          :model-value="allAgentsSelected"
          :indeterminate="someAgentsSelected"
          :title="$t('TEAMS_SETTINGS.AGENTS.SELECT_ALL')"
          @change="toggleSelectAll"
        />
      </div>
    </template>

    <template #row="{ items }">
      <BaseTableRow v-for="agent in items" :key="agent.id" :item="agent">
        <template #default>
          <BaseTableCell class="w-5">
            <div class="flex items-center">
              <Checkbox
                :model-value="isAgentSelected(agent.id)"
                @change="() => handleSelectAgent(agent.id)"
              />
            </div>
          </BaseTableCell>

          <BaseTableCell class="min-w-0 max-w-40">
            <div class="flex items-center gap-2 min-w-0">
              <Avatar
                :src="agent.thumbnail"
                :name="agent.name"
                :status="agent.availability_status"
                :size="24"
                hide-offline-status
                rounded-full
                class="flex-shrink-0"
              />
              <h4 class="text-heading-3 mb-0 text-n-slate-12 truncate">
                {{ agent.name }}
              </h4>
            </div>
          </BaseTableCell>

          <BaseTableCell class="min-w-0">
            <span class="text-body-main text-n-slate-11 truncate block">
              {{ agent.email || '---' }}
            </span>
          </BaseTableCell>
        </template>
      </BaseTableRow>
    </template>
  </BaseTable>

  <div
    class="flex items-center mt-4"
    :class="showSelectedCount ? 'justify-between' : 'justify-end'"
  >
    <p v-if="showSelectedCount" class="text-body-main text-n-slate-11">
      {{
        $t('TEAMS_SETTINGS.AGENTS.SELECTED_COUNT', {
          selected: selectedAgents.length,
          total: agentList.length,
        })
      }}
    </p>
    <NextButton
      type="submit"
      :label="submitButtonText"
      :disabled="disableSubmitButton"
      :is-loading="isWorking"
    />
  </div>
</template>
